import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import { deleteMatch, endMatch, fetchMatches } from "app/thunks/matchesAPI";
import { Match } from "interfaces/Match";
import { handleTableDataSorting, SortProps } from "utils/table/table";

import { RootState } from "../../app/store";

export interface State {
  list: Match[];
  isDeleting: boolean;
  isFinishing: boolean;
  isSuccess: boolean;
  isError: boolean;
  isFetching: boolean;
  error: any;
  selectedMatch?: Match;
}

const initialState: State = {
  list: [],
  isDeleting: false,
  isFinishing: false,
  isSuccess: false,
  isError: false,
  isFetching: false,
  error: undefined,
  selectedMatch: undefined,
};

export const matchesSlice = createSlice({
  name: "matches",
  initialState,
  reducers: {
    sortPlayers: (state, action: PayloadAction<SortProps>) => {
      const { sortBy, sortOrder } = action.payload;
      if (state.list.length > 0 && sortBy && sortOrder) {
        const sorted = [...state.list];
        sorted.sort(
          handleTableDataSorting<Match>({
            sortBy,
            sortOrder,
            dateProperties: ["createdAt"],
          }),
        );
        state.list = sorted;
      }
    },
    resetFlags: (state) => {
      state.isSuccess = false;
      state.isDeleting = false;
      state.isError = false;
      state.isFetching = false;
      state.selectedMatch = undefined;
    },
    setSelectedMatch(state, action: PayloadAction<Match>) {
      state.selectedMatch = action.payload;
    },
    unsetSelectedMatch(state) {
      state.selectedMatch = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetStore, () => initialState)
      .addCase(fetchMatches.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(
        fetchMatches.fulfilled,
        (state, action: PayloadAction<Match[]>) => {
          state.isSuccess = true;
          state.isFetching = false;
          state.list = action.payload;
        },
      )
      .addCase(deleteMatch.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteMatch.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (match) => match._id !== action.meta.arg,
        );
        state.isDeleting = false;
      })
      .addCase(deleteMatch.rejected, (state, action) => {
        state.isDeleting = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(endMatch.pending, (state) => {
        state.isFinishing = true;
      })
      .addCase(endMatch.fulfilled, (state) => {
        state.isFinishing = false;
      })
      .addCase(endMatch.rejected, (state, action) => {
        state.isFinishing = false;
        state.isError = true;
        state.error = action.payload;
      });
  },
});

export const { sortPlayers, resetFlags, setSelectedMatch, unsetSelectedMatch } =
  matchesSlice.actions;

// ------ Selectors ------
export const getMatches = (state: RootState) => ({
  matches: state.matches.list,
  isFetching: state.matches.isFetching,
});

export const getMatchesActionState = (state: RootState) => ({
  isDeleting: state.matches.isDeleting,
  isFinishing: state.matches.isFinishing,
  isSuccess: state.matches.isSuccess,
  isError: state.matches.isError,
  error: state.matches.error,
});

export const getSelectedMatch = (state: RootState) => ({
  selectedMatch: state.matches.selectedMatch,
});

export default matchesSlice.reducer;
